import toast from "react-hot-toast";
import { PaymentService } from "../Services/paymentService";


declare global {
    interface Window {
        Razorpay: any;
    }
}

export interface PaymentTransaction {
    orderId: string;
    amount: number;   // in rupees (as returned by the backend)
    currency: string;
    transactionId: string;
}


function loadScript(src: string): Promise<boolean> {
    return new Promise((resolve) => {
        const script = document.createElement("script");
        script.src = src;
        script.onload = () => resolve(true);
        script.onerror = () => resolve(false);
        document.body.appendChild(script);
    });
}


// Creates the order on the backend (also enrolls) and returns the transaction
// details — orderId / amount / currency — so the UI can show the fee before pay.
export async function createPaymentOrder(examId: string): Promise<PaymentTransaction | null> {
    const orderDetails: any = await PaymentService.createOrder(examId);

    // /examMapping/selfEnroll returns:
    // { data: { mapping, transaction: { orderId, amount, currency, transactionId } }, message, status }
    const transaction = orderDetails?.data?.data?.transaction;

    if (!transaction || !transaction.orderId) {
        return null;
    }

    return transaction as PaymentTransaction;
}


// Opens the Razorpay checkout for an already-created order/transaction.
// onVerifying(true) fires when payment verification starts (after the user pays)
// and onVerifying(false) when it ends — use it to show a loader, since
// verifyPayment can take a few seconds.
export async function openRazorpayCheckout(
    transaction: PaymentTransaction,
    prefill: { name: string; email: string; number: string; applicationno: string },
    onVerifying?: (verifying: boolean) => void,
) {
    const loaded = await loadScript("https://checkout.razorpay.com/v1/checkout.js");
    if (!loaded) {
        toast.error("Razorpay SDK failed to load");
        return;
    }

    const key = process.env.REACT_APP_RAZORPAYKEY;
    if (!key) {
        toast.error("Razorpay key is not configured");
        return;
    }

    // Razorpay expects the amount in the smallest currency unit (paise for INR).
    // The backend returns the fee in rupees, so convert to paise here.
    const amountInPaise = Math.round(transaction.amount * 100);

    const options = {
        key,
        amount: amountInPaise,
        currency: transaction.currency,
        name: prefill.name,
        description: `Fee: ${prefill.applicationno}`,
        order_id: transaction.orderId,

        handler: async (response: any) => {
            onVerifying?.(true);
            try {
                const result: any = await PaymentService.verifyPayment({
                    razorpay_payment_id: response.razorpay_payment_id,
                    razorpay_order_id: response.razorpay_order_id,
                    razorpay_signature: response.razorpay_signature,
                });

                if (result?.status === 200) {
                    toast.success("Payment Done!");
                    setTimeout(() => {
                        window.location.reload();
                    }, 1000);
                } else {
                    onVerifying?.(false);
                    toast.error("Payment Verification Failed!");
                }
            } catch (error) {
                onVerifying?.(false);
                console.error(error);
                toast.error("Payment verification error");
            }
        },

        prefill: {
            name: prefill.name,
            email: prefill.email,
            contact: prefill.number,
        },

        notes: {
            application: prefill.applicationno,
        },

        theme: {
            color: "#61dafb",
        },

        modal: {
            ondismiss: () => {
                toast.error("Payment cancelled");
            },
        },
    };

    const paymentObject = new window.Razorpay(options);

    paymentObject.on("payment.failed", (response: any) => {
        console.log("Payment Failed", response.error);
        toast.error("Payment Failed");
    });

    paymentObject.open();
    return paymentObject;
}


// Creates a group payment order and returns the transaction details
export async function createGroupPaymentOrder(groupId: string): Promise<PaymentTransaction | null> {
    const orderDetails: any = await PaymentService.createGroupPaymentOrder(groupId);
    const transaction = orderDetails?.data?.data?.transaction;

    if (!transaction || !transaction.orderId) {
        return null;
    }

    return transaction as PaymentTransaction;
}

// Opens Razorpay checkout for a group payment order
export async function openGroupRazorpayCheckout(
    transaction: PaymentTransaction,
    prefill: { name: string; email: string; number: string; applicationno: string },
    onVerifying?: (verifying: boolean) => void,
) {
    const loaded = await loadScript("https://checkout.razorpay.com/v1/checkout.js");
    if (!loaded) {
        toast.error("Razorpay SDK failed to load");
        return;
    }

    const key = process.env.REACT_APP_RAZORPAYKEY;
    if (!key) {
        toast.error("Razorpay key is not configured");
        return;
    }

    const amountInPaise = Math.round(transaction.amount * 100);

    const options = {
        key,
        amount: amountInPaise,
        currency: transaction.currency,
        name: prefill.name,
        description: `Group Fee: ${prefill.applicationno}`,
        order_id: transaction.orderId,

        handler: async (response: any) => {
            onVerifying?.(true);
            try {
                const result: any = await PaymentService.verifyGroupPayment({
                    razorpay_payment_id: response.razorpay_payment_id,
                    razorpay_order_id: response.razorpay_order_id,
                    razorpay_signature: response.razorpay_signature,
                });

                if (result?.status === 200) {
                    toast.success("Payment Done!");
                    setTimeout(() => {
                        window.location.reload();
                    }, 1000);
                } else {
                    onVerifying?.(false);
                    toast.error("Payment Verification Failed!");
                }
            } catch (error) {
                onVerifying?.(false);
                console.error(error);
                toast.error("Payment verification error");
            }
        },

        prefill: {
            name: prefill.name,
            email: prefill.email,
            contact: prefill.number,
        },

        notes: {
            application: prefill.applicationno,
        },

        theme: {
            color: "#61dafb",
        },

        modal: {
            ondismiss: () => {
                toast.error("Payment cancelled");
            },
        },
    };

    const paymentObject = new window.Razorpay(options);

    paymentObject.on("payment.failed", (response: any) => {
        console.log("Payment Failed", response.error);
        toast.error("Payment Failed");
    });

    paymentObject.open();
    return paymentObject;
}

// Convenience: create the order and immediately open checkout (no confirm step).
export async function displayRazorpay(
    name: string,
    email: string,
    number: string,
    applicationno: string,
    examId: string,
) {
    try {
        const transaction = await createPaymentOrder(examId);
        if (!transaction) {
            toast.error("Failed to create order");
            return;
        }
        return await openRazorpayCheckout(transaction, { name, email, number, applicationno });
    } catch (error) {
        console.error(error);
        toast.error("Something went wrong");
    }
}
